import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Clock, LogOut, Zap, Infinity as InfinityIcon, Trophy, Calendar,
  Plus, ShoppingBag, History, CheckCircle2, X, AlertCircle, ChevronRight,
} from "lucide-react";

const TYPE_META = {
  hours:     { icon: Clock,   label: "hours",    unit: "hrs" },
  overs:     { icon: Zap,     label: "overs",    unit: "overs" },
  sessions:  { icon: Trophy,  label: "sessions", unit: "sessions" },
  unlimited: { icon: InfinityIcon, label: "unlimited", unit: "unlimited" },
};

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("memberships");
  const [summary, setSummary] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [reserveModal, setReserveModal] = useState(null); // membership
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/member/login"); return; }
    reload();
  }, [user, navigate]);

  const reload = async () => {
    setLoading(true);
    const [s, m, r] = await Promise.all([
      api.get("/member/summary").then((x) => x.data),
      api.get("/member/memberships").then((x) => x.data),
      api.get("/member/reservations").then((x) => x.data).catch(() => []),
    ]);
    setSummary(s); setMemberships(m); setReservations(r);
    setLoading(false);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0B1120]" data-testid="member-dashboard">
      <RenewalBanner memberships={memberships} onRenew={() => navigate("/membership")} />
      {/* header */}
      <div className="border-b border-white/8 bg-[#0F172A]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 flex items-center justify-between gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] brand-orange">// Member Portal</div>
            <h1 className="font-display text-2xl md:text-3xl font-black uppercase mt-1">Hello, {user?.name?.split(" ")[0]}</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/membership" className="hidden sm:inline-flex px-3 py-2 border border-white/15 text-[11px] font-mono uppercase tracking-widest hover:border-brand-blue items-center gap-1.5">
              <Plus size={12} /> Buy Plan
            </Link>
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="member-logout" className="px-3 py-2 border border-red-500/40 text-red-400 text-[11px] font-mono uppercase tracking-widest hover:bg-red-500 hover:text-white inline-flex items-center gap-1.5">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* stat strip */}
      <div className="max-w-6xl mx-auto px-5 md:px-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="member-stats">
        <Stat label="Active memberships" value={summary?.memberships_active ?? 0} />
        <Stat label="Upcoming reservations" value={summary?.upcoming_reservations ?? 0} />
        <Stat label="Total memberships" value={summary?.memberships_total ?? 0} />
        <Stat label="Total spent" value={formatINR(summary?.total_spent || 0)} tone="orange" />
      </div>

      {/* tabs */}
      <div className="max-w-6xl mx-auto px-5 md:px-8 mt-8 border-b border-white/10 flex gap-2 overflow-x-auto">
        <TabBtn active={tab === "memberships"} onClick={() => setTab("memberships")} testid="tab-memberships">
          <ShoppingBag size={12} /> My Memberships
        </TabBtn>
        <TabBtn active={tab === "reservations"} onClick={() => setTab("reservations")} testid="tab-reservations">
          <Calendar size={12} /> My Reservations
        </TabBtn>
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 pb-16">
        {loading ? <div className="text-white/40 py-16 text-center text-sm font-mono uppercase tracking-widest">Loading…</div> : (
          tab === "memberships" ? (
            <MembershipsTab memberships={memberships} onReserve={setReserveModal} />
          ) : (
            <ReservationsTab reservations={reservations} />
          )
        )}
      </div>

      {reserveModal && (
        <ReserveModal membership={reserveModal} onClose={() => setReserveModal(null)} onDone={() => { setReserveModal(null); reload(); }} />
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`border border-white/12 bg-[#131A2B] p-4 ${tone === "orange" ? "border-brand-orange/40" : ""}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-display text-2xl font-black mt-1 ${tone === "orange" ? "brand-orange" : "brand-blue"}`}>{value}</div>
    </div>
  );
}

function RenewalBanner({ memberships, onRenew }) {
  // Show the earliest-expiring active membership that expires in ≤ 14 days.
  const soon = (memberships || [])
    .filter((m) => m.is_active && m.expires_in_days != null && m.expires_in_days <= 14 && m.expires_in_days >= 0)
    .sort((a, b) => (a.expires_in_days || 0) - (b.expires_in_days || 0));
  if (soon.length === 0) return null;
  const m = soon[0];
  const urgent = m.expires_in_days <= 3;
  return (
    <div
      data-testid="renewal-banner"
      className={`px-5 md:px-8 py-3 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 ${
        urgent ? "bg-red-500 text-white" : "bg-brand-orange text-black"
      }`}
    >
      <div className="flex items-start gap-3">
        <AlertCircle size={18} className="shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-black uppercase text-sm md:text-base">
            {urgent ? "Renew now" : "Renewal reminder"} — {m.facility_name} · {m.plan_snapshot?.name}
          </div>
          <div className="text-xs md:text-sm mt-0.5">
            Expires in <strong>{m.expires_in_days}</strong> day{m.expires_in_days === 1 ? "" : "s"}. Don't lose your balance & priority booking.
          </div>
        </div>
      </div>
      <button
        onClick={onRenew}
        data-testid="renewal-cta"
        className={`px-4 py-2 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2 whitespace-nowrap ${
          urgent ? "bg-white text-red-600 hover:bg-black hover:text-white" : "bg-black text-white hover:bg-white hover:text-black"
        }`}
      >
        Renew Now <ChevronRight size={12} />
      </button>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2.5 text-[11px] font-mono uppercase tracking-widest border-b-2 flex items-center gap-2 whitespace-nowrap ${
        active ? "border-brand-orange text-white" : "border-transparent text-white/50 hover:text-white"
      }`}>{children}</button>
  );
}

function MembershipsTab({ memberships, onReserve }) {
  if (memberships.length === 0) return (
    <div className="border border-dashed border-white/15 p-10 text-center">
      <div className="text-white/60 mb-3">You don't have any memberships yet.</div>
      <Link to="/membership" className="inline-flex items-center gap-2 bg-brand-orange text-black px-5 py-3 font-bold text-xs uppercase tracking-widest">
        Browse Plans <ChevronRight size={14} />
      </Link>
    </div>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-memberships">
      {memberships.map((m) => <MembershipCard key={m.id} m={m} onReserve={onReserve} />)}
    </div>
  );
}

function MembershipCard({ m, onReserve }) {
  const type = (m.plan_snapshot?.plan_type) || "hours";
  const meta = TYPE_META[type] || TYPE_META.hours;
  const Icon = meta.icon;
  const isUnlim = type === "unlimited";
  const total = m.quota_total; const used = m.quota_used || 0;
  const pct = isUnlim ? 100 : (total ? Math.min(100, Math.round((used / total) * 100)) : 0);
  const remaining = isUnlim ? "∞" : (m.quota_remaining ?? "—");
  const validUntil = m.valid_until ? new Date(m.valid_until).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className={`border ${m.is_active ? "border-brand-blue/40 bg-brand-blue/5" : "border-white/10 bg-black/20 opacity-70"} p-5`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{m.facility_name}</div>
          <div className="font-display text-lg font-black uppercase mt-1">{m.plan_snapshot?.name || "Membership"}</div>
        </div>
        <div className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
          m.is_active ? "border-green-500/50 text-green-400" : "border-red-500/50 text-red-400"
        }`}>
          {m.is_active ? "Active" : (m.expires_in_days != null && m.expires_in_days < 0 ? "Expired" : "Inactive")}
        </div>
      </div>

      {/* progress */}
      <div className="mt-4" data-testid={`membership-usage-${m.id}`}>
        <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-widest mb-1.5">
          <span className="text-white/60 flex items-center gap-1.5"><Icon size={12} className="brand-blue" /> Balance</span>
          <span className="brand-blue">{remaining} {isUnlim ? "" : meta.unit}</span>
        </div>
        <div className="h-2 bg-white/10 overflow-hidden relative">
          <div className={`h-full transition-all ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-brand-orange" : "bg-brand-blue"}`}
               style={{ width: `${isUnlim ? 100 : Math.max(2, 100 - pct)}%` }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[10px] font-mono">
          <span className="text-white/40">
            {isUnlim ? "Unlimited use" : `Used ${used} of ${total} ${meta.unit}`}
          </span>
          <span className={`${pct >= 90 ? "text-red-400" : pct >= 70 ? "brand-orange" : "text-white/50"}`}>
            {isUnlim ? "" : `${pct}% used`}
          </span>
        </div>
        {m.plan_snapshot?.max_units_per_day && !isUnlim && (
          <div className="mt-1 text-[10px] font-mono text-white/40" data-testid={`membership-daily-cap-${m.id}`}>
            Daily cap: {m.plan_snapshot.max_units_per_day} {meta.unit}/day
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest">
        <span className="text-white/50">Valid until</span>
        <span className="text-white/85">{validUntil}</span>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          onClick={() => onReserve(m)}
          disabled={!m.is_active}
          data-testid={`reserve-slot-${m.id}`}
          className="flex-1 bg-brand-orange text-black py-2.5 font-bold text-[11px] uppercase tracking-widest hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 justify-center"
        >
          <Calendar size={12} /> Reserve Slot
        </button>
        <Link to="/membership" className="px-3 border border-white/15 text-[11px] font-mono uppercase tracking-widest inline-flex items-center hover:border-brand-blue">Renew</Link>
      </div>
    </div>
  );
}

function ReservationsTab({ reservations }) {
  if (reservations.length === 0) return (
    <div className="border border-dashed border-white/15 p-10 text-center text-white/60">No reservations yet.</div>
  );
  return (
    <div className="border border-white/10 overflow-hidden" data-testid="my-reservations">
      <table className="w-full text-sm">
        <thead className="bg-white/5">
          <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
            <th className="px-4 py-3">Facility</th>
            <th className="px-4 py-3">Resource</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Hour</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {reservations.map((r) => (
            <tr key={r.id} className="border-t border-white/6">
              <td className="px-4 py-3 font-medium">{r.service_slug}</td>
              <td className="px-4 py-3 text-white/70">{r.resource}</td>
              <td className="px-4 py-3 font-mono">{r.date}</td>
              <td className="px-4 py-3 font-mono">{r.start_hour}:00 – {r.start_hour + r.duration}:00</td>
              <td className="px-4 py-3">
                <span className="text-[10px] font-mono uppercase tracking-widest text-green-400 border border-green-500/50 px-2 py-0.5 inline-flex items-center gap-1">
                  <CheckCircle2 size={10} /> {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReserveModal({ membership, onClose, onDone }) {
  const [svc, setSvc] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [avail, setAvail] = useState(null);
  const [resource, setResource] = useState("");
  const [startHour, setStartHour] = useState(null);
  const [duration, setDuration] = useState(1);
  const [busy, setBusy] = useState(false);
  const [reservedQR, setReservedQR] = useState(null);

  useEffect(() => {
    api.get(`/services/${membership.facility_slug}`)
      .then((r) => {
        setSvc(r.data);
        setResource((r.data.resources || [])[0] || "");
      })
      .catch(() => toast.error("Facility is currently unavailable"));
  }, [membership.facility_slug]);

  useEffect(() => {
    if (!svc || !date) return;
    api.get(`/availability?service_slug=${membership.facility_slug}&date=${date}`)
      .then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [svc, date, membership.facility_slug]);

  const hours = avail?.hours || [];
  const matrix = avail?.matrix || {};
  const busyHours = useMemo(() => {
    if (!resource || !matrix[resource]) return new Set();
    return new Set(Object.entries(matrix[resource]).filter(([, v]) => v).map(([h]) => parseInt(h)));
  }, [matrix, resource]);

  const submit = async () => {
    if (startHour == null) { toast.error("Pick an hour"); return; }
    setBusy(true);
    try {
      await api.post("/member/reserve", {
        membership_id: membership.id, resource, date,
        start_hour: startHour, duration,
      });
      toast.success("Slot reserved!");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reservation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#131A2B] border border-white/12 my-8" onClick={(e) => e.stopPropagation()} data-testid="reserve-modal">
        {reservedQR ? (
          <>
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">Reserved!</div>
                <div className="font-display text-lg font-black uppercase mt-0.5">Show QR at reception</div>
              </div>
              <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5">
              <div className="flex justify-center mb-4">
                <div className="bg-white p-3 inline-block" data-testid="reserved-qr">
                  <QRCodeSVG value={reservedQR.qr_token || ""} size={168} level="M" />
                </div>
              </div>
              <div className="text-center text-[11px] font-mono uppercase tracking-widest text-white/60 mb-4">
                {membership.facility_name} · {reservedQR.resource} · {reservedQR.date} · {reservedQR.start_hour}:00 → {reservedQR.start_hour + reservedQR.duration}:00
              </div>
              <div className="text-center font-mono text-xs text-white/50 break-all">
                Token: <span className="brand-blue">{reservedQR.qr_token}</span>
              </div>
            </div>
            <div className="p-5 border-t border-white/10">
              <button onClick={onDone} data-testid="reserve-done-btn"
                className="w-full bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white">
                Done
              </button>
            </div>
          </>
        ) : (
        <>
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">Reserve Slot</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">{membership.facility_name}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {!svc && <div className="text-red-400 text-sm flex gap-2 items-start"><AlertCircle size={14} className="mt-0.5" /> Facility is unavailable right now.</div>}
          {svc && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Date</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" data-testid="reserve-date" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Resource</label>
                  <select value={resource} onChange={(e) => setResource(e.target.value)} data-testid="reserve-resource"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
                    {(svc.resources || []).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Available hours</label>
                <div className="flex flex-wrap gap-1.5">
                  {hours.map((h) => {
                    const isBusy = busyHours.has(h);
                    const sel = startHour === h;
                    return (
                      <button key={h}
                        disabled={isBusy}
                        onClick={() => setStartHour(h)}
                        data-testid={`hour-btn-${h}`}
                        className={`px-2.5 py-1.5 border text-xs font-mono transition ${
                          isBusy ? "border-white/8 text-white/25 line-through cursor-not-allowed" :
                          sel ? "border-brand-orange bg-brand-orange text-black" : "border-white/20 hover:border-brand-blue"
                        }`}>{h}:00</button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Duration (hours)</label>
                <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" data-testid="reserve-duration">
                  {[1,2,3].map((d) => <option key={d} value={d}>{d}h</option>)}
                </select>
              </div>
            </>
          )}
        </div>
        <div className="p-5 border-t border-white/10 flex gap-2">
          <button onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button onClick={submit} disabled={busy || !svc} data-testid="reserve-submit"
            className="flex-1 bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white disabled:opacity-50">
            {busy ? "Reserving…" : "Confirm Reservation"}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
